import {
  DiscoveryApi,
  IdentityApi,
  ConfigApi,
} from '@backstage/core-plugin-api';
import {
  GetMeasurementResultsOptions,
  RefreshOptions,
  RefreshResponse,
  SimulateOptions,
  YardstickApi,
  GetExceptionsOptions,
  ExceptionResult,
  GetIssuesV1Result,
  GetChecksV1,
  ResultsV1,
  Measurement,
  GetIssuesV2Result,
} from './yardstick-api';
import type {
  FindingsSummaryRequest,
  FindingsSummaryResponse,
  FindingsListRequest,
  FindingsListResponse,
  FindingDetailRequest,
  FindingDetailResponse,
  EntitySummaryRequest,
  EntitySummaryResponse,
  HistoryComparisonRequest,
  HistoryComparisonResponse,
  HistorySeriesRequest,
  HistorySeriesResponse,
  InsightsScoreResponse,
} from '@internal/yardstick-common';
import {
  Task,
  AutofixStepResult,
  AutofixStepExecutionOutput,
  AutofixV2ExecuteStepRequest,
  DownloadRequestOptions,
  DownloadRequestResponse,
  DownloadStatusResponse,
  Visibility,
} from '@internal/yardstick-common';
import { CategoryNode } from '../utils/category';
import pLimit from 'p-limit';
import { MetricData } from '../InsightsAndActions/charts/components/trend-view';
import { fetchWithTracingAndRetries } from '@internal/plugin-pdp-core-api';
import { PdpLoggerApi } from '@internal/plugin-pdp-logger-frontend';
import { getEnv } from '../utils/env';

export class YardstickClient implements YardstickApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private readonly configApi: ConfigApi;
  private readonly pdpLoggerApi: PdpLoggerApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    configApi: ConfigApi;
    pdpLoggerApi: PdpLoggerApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
    this.configApi = options.configApi;
    this.pdpLoggerApi = options.pdpLoggerApi;
  }

  private async getYardstickBaseUrl() {
    const serviceUrl = await this.discoveryApi.getBaseUrl('yardstick');

    // TODO: Don't like having to replace, but need to figure out how to
    // pass our custom discovery api to the *client*.
    // Replace the port when developing locally since Yardstick runs on 7008.
    return serviceUrl.replace('7007', '7008');
  }

  private async getToken() {
    const credentials = await this.identityApi.getCredentials();

    return credentials.token;
  }

  async getMeasurementResults(
    options?: GetMeasurementResultsOptions,
  ): Promise<ResultsV1> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/results`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...(options ?? {}) }),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch measurement results');
    }

    return response.json();
  }

  async getIssuesV1(
    entities: string[],
    options?: {
      focus?: string[];
      severity?: string[];
      measurementChecks?: string[];
    },
    adminShowIssues?: boolean,
    volumeTesting?: boolean,
    visibility?: Visibility[],
  ): Promise<GetIssuesV1Result> {
    const baseUrl = await this.getYardstickBaseUrl();
    const retrievePageMeasurements = true;
    const chunkSize = 1000;
    // Split entities into chunks of 1000
    const entityChunks = [];
    for (let i = 0; i < entities.length; i += chunkSize) {
      entityChunks.push(entities.slice(i, i + chunkSize));
    }

    const environment = getEnv();

    let volumeTestingTotal = 80000;

    if (environment !== 'local') {
      volumeTestingTotal = 80000;
    }

    const fetchChunks = entityChunks;

    const limit = pLimit(5); // Reduced from 10 to 5 for container memory efficiency
    let total = 0;
    let failedChunks = 0;
    const mergedEntities = new Map<string, any>(); // Use Map for merged entities

    do {
      const results = await Promise.all(
        fetchChunks.map(chunk =>
          limit(async () => {
            const response = await fetchWithTracingAndRetries(
              `${baseUrl}/v1/issues`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${await this.getToken()}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  entities: chunk,
                  focus: options?.focus,
                  severity: options?.severity,
                  measurementChecks: options?.measurementChecks,
                  adminShowIssues,
                  retrievePageMeasurements,
                  visibility: visibility ?? [],
                }),
              },
              this.pdpLoggerApi,
            );

            if (response.ok) {
              return response.json();
            }

            return undefined; // Return undefined if all attempts fail
          }),
        ),
      );

      for (const result of results) {
        if (!result || !result.entities) {
          failedChunks++;
          continue; // Skip this chunk if it has no entities
        }
        const entitiesArray = Array.isArray(result.entities)
          ? result.entities
          : Object.values(result.entities ?? {});
        for (const entity of entitiesArray) {
          total += entity.issues.length;
          if (!mergedEntities.has(entity.entity.ref)) {
            mergedEntities.set(entity.entity.ref, { ...entity, issues: [] });
          }
          mergedEntities.get(entity.entity.ref).issues.push(...entity.issues);
        }
      }
    } while (volumeTesting && total < volumeTestingTotal);

    const response = {
      entities: Object.fromEntries(mergedEntities), // Convert Map to object
      _meta: {
        total,
        failedChunks,
      },
    } as GetIssuesV1Result;

    return response;
  }

  async getIssuesV2(
    entities: string[],
    options?: {
      focus?: string[];
      severity?: string[];
      measurementChecks?: string[];
      grouping?: 'none' | 'check';
    },
    adminShowIssues?: boolean,
    volumeTesting?: boolean,
    visibility?: Visibility[],
  ): Promise<GetIssuesV2Result> {
    const baseUrl = await this.getYardstickBaseUrl();
    const retrievePageMeasurements = true;
    const chunkSize = 1000;
    // Split entities into chunks of 1000
    const entityChunks = [];
    for (let i = 0; i < entities.length; i += chunkSize) {
      entityChunks.push(entities.slice(i, i + chunkSize));
    }

    const environment = getEnv();

    let volumeTestingTotal = 80000;

    if (environment !== 'local') {
      volumeTestingTotal = 80000;
    }

    const fetchChunks = entityChunks;

    const limit = pLimit(5); // Reduced from 10 to 5 for container memory efficiency
    let totalIssues = 0;
    let totalGroups = 0;
    let ungroupedIssues = 0;
    let groupedIssues = 0;
    let failedChunks = 0;
    const mergedEntities = new Map<string, any>(); // Use Map for merged entities

    do {
      const results = await Promise.all(
        fetchChunks.map(chunk =>
          limit(async () => {
            const response = await fetchWithTracingAndRetries(
              `${baseUrl}/v2/issues`,
              {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${await this.getToken()}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  entities: chunk,
                  focus: options?.focus,
                  severity: options?.severity,
                  measurementChecks: options?.measurementChecks,
                  grouping: options?.grouping,
                  adminShowIssues,
                  retrievePageMeasurements,
                  visibility: visibility ?? [],
                }),
              },
              this.pdpLoggerApi,
            );

            if (response.ok) {
              return response.json();
            }

            return undefined; // Return undefined if all attempts fail
          }),
        ),
      );

      for (const result of results) {
        if (!result || !result.entities) {
          failedChunks++;
          continue; // Skip this chunk if it has no entities
        }
        totalIssues += result._meta.totalIssues;
        totalGroups += result._meta.totalGroups;
        ungroupedIssues += result._meta.ungroupedIssues;
        groupedIssues += result._meta.groupedIssues;
        const entitiesArray = Array.isArray(result.entities)
          ? result.entities
          : Object.values(result.entities ?? {});
        for (const entity of entitiesArray) {
          if (!mergedEntities.has(entity.entity.ref)) {
            mergedEntities.set(entity.entity.ref, { ...entity, issues: [] });
          }
          mergedEntities.get(entity.entity.ref).issues.push(...entity.issues);
        }
      }
    } while (volumeTesting && totalIssues < volumeTestingTotal);

    const response = {
      entities: Object.fromEntries(mergedEntities), // Convert Map to object
      _meta: {
        totalIssues,
        totalGroups,
        ungroupedIssues,
        groupedIssues,
        failedChunks,
      },
    } as GetIssuesV2Result;

    return response;
  }

  async refresh(options: RefreshOptions): Promise<RefreshResponse> {
    const baseUrl = await this.getYardstickBaseUrl();
    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/measurements/refresh`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to refresh measurements');
    }

    const { data } = await response.json();

    return data;
  }

  async getTaskStatus(taskId: string): Promise<Task> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/tasks/${taskId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
        },
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to get task status');
    }

    return response.json();
  }

  async simulate(options: SimulateOptions): Promise<any> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/simulator/simulate/${options.type}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: options.data }),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to simulate');
    }

    return response.json();
  }

  async getExceptions(
    options?: GetExceptionsOptions,
  ): Promise<ExceptionResult[]> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/exceptions`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options ?? {}),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch exceptions');
    }

    const data = await response.json();
    const exceptionresult = data.data as ExceptionResult[];
    return exceptionresult;
  }

  async getMeasurements(): Promise<Measurement[]> {
    const baseUrl = await this.getYardstickBaseUrl();
    const measurements: Measurement[] = [];
    const pageSize = 1000;
    let page = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await fetchWithTracingAndRetries(
        `${baseUrl}/v1/measurements?page=${page}&page-size=${pageSize}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${await this.getToken()}`,
            'Content-Type': 'application/json',
          },
        },
        this.pdpLoggerApi,
      );

      if (!response.ok) {
        throw new Error('Failed to fetch measurements');
      }

      const data = await response.json();
      const batch: Measurement[] = data?.data ?? [];
      measurements.push(...batch);

      if (batch.length < pageSize) {
        hasMorePages = false;
      } else {
        page += 1;
      }
    }

    return measurements;
  }

  async getMeasurementRegistryResults(): Promise<CategoryNode[]> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/check/registry`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          visibility: ['siteAndEntity'],
        }),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch measurement registry');
    }

    const data = await response.json();
    const registryData = data.data as CategoryNode[];
    return registryData;
  }

  async getEntityPageRegistryResults(): Promise<CategoryNode[]> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/check/registry`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          visibility: ['siteAndEntity', 'entity-only'],
        }),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch entity page registry');
    }

    const data = await response.json();
    const registryData = data.data as CategoryNode[];
    return registryData;
  }

  async getChecks(): Promise<GetChecksV1> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/checks`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch checks');
    }
    const data = await response.json();

    return data;
  }

  async getTrendMetrics(
    entities: string[],
    focus: string[],
  ): Promise<MetricData[]> {
    const { token } = await this.identityApi.getCredentials();
    const url = this.configApi.getString('backend.baseUrl');
    const response = await fetchWithTracingAndRetries(
      `${url}/api/pattern/check-insights`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entityRefs: entities, focus }),
      },
      this.pdpLoggerApi,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to retrieve trend metrics info, ${response.status} ${response.statusText}`,
      );
    }
    const data = await response.json();
    return data?.data ?? [];
  }

  /**
   * Get autofix step results for a specific issue
   */
  async getAutofixV2StepResults(issueId: string): Promise<AutofixStepResult[]> {
    const baseUrl = await this.getYardstickBaseUrl();
    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v2/autofix/issue/${encodeURIComponent(issueId)}/steps`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      if (response.status === 404) {
        // No results yet - this is expected for new issues
        return [];
      }
      throw new Error(
        `Failed to fetch autofix step results: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.data || ([] as AutofixStepResult[]);
  }

  /**
   * Execute an autofix step for a specific issue
   */
  async executeAutofixV2Step(
    issueId: string,
    request: AutofixV2ExecuteStepRequest,
  ): Promise<AutofixStepExecutionOutput> {
    const baseUrl = await this.getYardstickBaseUrl();
    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v2/autofix/issue/${encodeURIComponent(issueId)}/execute`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error(`Failed to execute autofix step: ${response.statusText}`);
    }

    const data = await response.json();
    return data as AutofixStepExecutionOutput;
  }

  async executeAutofixV2StepBulk(
    issueIds: string[],
    stepName: string,
    params?: Record<string, any>,
    userToken?: string,
  ): Promise<AutofixStepExecutionOutput> {
    const baseUrl = await this.getYardstickBaseUrl();
    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v2/autofix/issues/execute`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          issueIds,
          stepName,
          params: params || {},
          userToken,
        }),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      // Parse response body for detailed error message
      let errorMessage = `Failed to execute bulk autofix step (${response.status})`;
      try {
        const errorData = await response.json();
        if (errorData?.error || errorData?.message) {
          errorMessage = `${errorData.error || 'Error'}: ${
            errorData.message || response.statusText
          }`;
        }
      } catch {
        // If JSON parsing fails, fall back to statusText
        errorMessage = `Failed to execute bulk autofix step: ${
          response.statusText || response.status
        }`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data as AutofixStepExecutionOutput;
  }

  async requestDownload(
    options: DownloadRequestOptions,
  ): Promise<DownloadRequestResponse> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/download/request`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to submit download request');
    }

    return response.json();
  }

  async checkDownloadStatus(
    requestId: string,
    signal?: AbortSignal,
  ): Promise<DownloadStatusResponse> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/download/${requestId}/status`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        signal,
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to check download status');
    }

    return response.json();
  }

  async streamDownload(requestId: string, signal?: AbortSignal): Promise<Blob> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/download/${requestId}/stream`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
        },
        signal,
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to download file');
    }

    return response.blob();
  }

  async getIssuesSummary(
    request: FindingsSummaryRequest,
  ): Promise<FindingsSummaryResponse> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v3/issues/summary`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch issues summary');
    }

    return response.json();
  }

  private async postInsights<T>(
    path: string,
    body: unknown,
    label: string,
  ): Promise<T> {
    const baseUrl = await this.getYardstickBaseUrl();
    const response = await fetchWithTracingAndRetries(
      `${baseUrl}${path}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
      this.pdpLoggerApi,
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch ${label} (${response.status})`);
    }
    return response.json();
  }

  async getInsightsScore(request: {
    entities: string[];
  }): Promise<InsightsScoreResponse> {
    return this.postInsights('/v3/insights/score', request, 'insights score');
  }

  async getInsightsHistoryComparison(
    request: HistoryComparisonRequest,
  ): Promise<HistoryComparisonResponse> {
    return this.postInsights(
      '/v3/insights/history/comparison',
      request,
      'insights history comparison',
    );
  }

  async getInsightsHistorySeries(
    request: HistorySeriesRequest,
  ): Promise<HistorySeriesResponse> {
    return this.postInsights(
      '/v3/insights/history/series',
      request,
      'insights history series',
    );
  }

  async getIssuesList(
    request: FindingsListRequest,
    signal?: AbortSignal,
  ): Promise<FindingsListResponse> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v3/issues/list`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal,
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch issues list');
    }

    return response.json();
  }

  async getIssuesDetail(
    request: FindingDetailRequest,
  ): Promise<FindingDetailResponse> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v3/issues/detail`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch issue detail');
    }

    return response.json();
  }

  async getEntitySummary(
    request: EntitySummaryRequest,
    signal?: AbortSignal,
  ): Promise<EntitySummaryResponse> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v3/issues/entity-summary`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal,
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch entity summary');
    }

    return response.json();
  }

  async deferBudget(options: {
    deferEndpoint: string;
    entityRef: string;
  }): Promise<void> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v1/budget/defer`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      let code = 'PROCESSING_ERROR';
      try {
        const body = await response.json();
        code = body?.code ?? code;
      } catch {
        // ignore parse failure
      }
      const err = new Error(code) as Error & { code: string };
      err.code = code;
      throw err;
    }
  }

  async markAsRemediated(options: {
    entityRef: string;
    issueId: string;
  }): Promise<void> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v3/issues/remediate`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requests: [
            {
              id: options.issueId,
              entity_ref: options.entityRef,
            },
          ],
        }),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to mark finding as remediated');
    }
  }

  async undoMarkAsRemediated(options: {
    entityRef: string;
    issueId: string;
  }): Promise<void> {
    const baseUrl = await this.getYardstickBaseUrl();

    const response = await fetchWithTracingAndRetries(
      `${baseUrl}/v3/issues/remediate`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${await this.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requests: [
            {
              id: options.issueId,
              entity_ref: options.entityRef,
            },
          ],
        }),
      },
      this.pdpLoggerApi,
    );

    if (!response.ok) {
      throw new Error('Failed to undo mark as remediated');
    }
  }
}
